import React, { useState } from 'react';

const TeacherHelperGame = ({ gameData, onComplete }) => {
    const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [sortedItems, setSortedItems] = useState([]);

    const currentTask = gameData?.tasks?.[currentTaskIndex];

    React.useEffect(() => {
        if (currentTask?.type === 'sorting') {
            setSortedItems([...currentTask.items]);
        }
    }, [currentTaskIndex, currentTask]);

    const handleGrading = (userAnswer) => {
        if (feedback) return;
        const isCorrect = userAnswer === currentTask.is_correct;

        if (isCorrect) {
            const pointsToAdd = gameData.points_per_task || 15;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            setFeedback({ type: 'correct', message: 'Tobulai pataisyta! 👩‍🏫' });
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            setTimeout(nextTask, 3000);
        } else {
            setFeedback({ type: 'error', message: 'Oi! Tai dar ne visai tikslu. Pabandyk dar kartą! ❌' });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    const handleSortingSubmit = () => {
        if (feedback) return;
        const isCorrect = JSON.stringify(sortedItems) === JSON.stringify(currentTask.correct_order);

        if (isCorrect) {
            const pointsToAdd = gameData.points_per_task || 15;
            const newScore = score + pointsToAdd;
            setScore(newScore);
            setFeedback({ type: 'correct', message: 'Idealus išrūšiavimas! 📚' });
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
            setTimeout(nextTask, 3000);
        } else {
            setFeedback({ type: 'error', message: 'Eilės tvarka dar nėra teisinga! Bandyk dar kartą. ❌' });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    const moveItem = (index, direction) => {
        if (feedback) return;
        const newItems = [...sortedItems];
        const newIndex = index + direction;

        if (newIndex >= 0 && newIndex < newItems.length) {
            [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
            setSortedItems(newItems);
            if (navigator.vibrate) navigator.vibrate(30);
        }
    };

    const nextTask = () => {
        if (currentTaskIndex < gameData.tasks.length - 1) {
            setCurrentTaskIndex(prev => prev + 1);
            setFeedback(null);
            if (navigator.vibrate) navigator.vibrate(30);
        } else {
            if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 300]);
            onComplete?.(score);
        }
    };

    if (!gameData || !gameData.tasks || !currentTask) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-purple-100 animate-pulse bg-white">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">👩‍🏫</div>
                <h3 className="text-base md:text-3xl font-black text-purple-900 tracking-tighter uppercase">Rušiuojami darbai...</h3>
            </div>
        );
    }

    const progress = (currentTaskIndex / gameData.tasks.length) * 100;

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Academy HUD */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-purple-50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-2 md:p-6">
                        <div className="w-20 h-20 bg-purple-100 rounded-3xl flex items-center justify-center text-lg md:text-4xl shadow-inner border-2 border-purple-200 animate-float">
                            👩‍🏫
                        </div>
                        <div>
                            <h3 className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter leading-tight uppercase">
                                Mokytojo <span className="text-purple-600">Pagalbininkas</span>
                            </h3>
                            <p className="text-slate-500 font-bold italic font-serif">
                                Padėk sutvarkyti klasę ir ištaisyti mokinių darbus!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 md:p-6">
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Prestižas</p>
                            <p className="text-base md:text-3xl font-black text-purple-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-8 py-4 rounded-[2rem] border-2 border-white shadow-inner text-center min-w-[120px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2">Užduotis</p>
                            <p className="text-base md:text-3xl font-black text-slate-800 leading-none">{currentTaskIndex + 1}/{gameData.tasks.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-8 w-full bg-slate-200/50 rounded-full h-4 p-1 shadow-inner overflow-hidden border-2 border-slate-300">
                    <div 
                        className="h-full bg-gradient-to-r from-purple-400 via-pink-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(168,85,247,0.4)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            {/* Academy Stage Interaction Area */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-16 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center min-h-[50vh] justify-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none rotate-12">📚</div>
                <div className="absolute bottom-0 left-0 p-4 md:p-12 text-2xl md:text-6xl md:text-[200px] opacity-[0.03] select-none pointer-events-none -rotate-12 text-purple-600">✏️</div>
                
                <div className="w-full max-w-5xl space-y-16 relative z-10">
                    <div className="text-[160px] drop-shadow-[0_40px_80px_rgba(168,85,247,0.3)] animate-float mb-8">{currentTask.emoji}</div>

                    {currentTask.type === 'grading' ? (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-6">
                                <h4 className="text-[10px] font-black text-purple-500 uppercase tracking-[0.6em] mb-4">Analizės Centras</h4>
                                <div className="bg-slate-950 text-white rounded-2xl md:rounded-[4rem] p-4 md:p-12 lg:p-4 md:p-20 shadow-2xl border-[10px] border-slate-900 relative group overflow-hidden">
                                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.05)_0%,transparent_100%)] opacity-30" />
                                    <div className="absolute top-0 right-0 p-4 md:p-10 text-8xl opacity-10 select-none pointer-events-none group-hover:scale-125 transition-transform">📋</div>
                                    <p className="text-xl md:text-5xl lg:text-8xl font-black tracking-tighter font-mono relative z-10 drop-shadow-[0_0_30px_rgba(255,255,255,0.2)] uppercase">
                                        {currentTask.question}
                                    </p>
                                    <div className="mt-8 border-t-2 border-white/10 pt-6">
                                        <p className="text-xl font-bold text-purple-400 italic font-serif">"Ar šis atsakymas tikrai teisingas?"</p>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:p-8 max-w-4xl mx-auto">
                                <button
                                    onClick={() => handleGrading(true)}
                                    className="premium-button-3d py-4 md:py-10 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-600 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-[0_20px_50px_rgba(16,185,129,0.3)] uppercase tracking-[0.2em] flex items-center justify-center gap-2 md:p-6 border-b-[12px] border-emerald-800 active:translate-y-2 active:border-b-0"
                                >
                                    TEISINGAI <span className="text-xl md:text-5xl animate-bounce">✓</span>
                                </button>
                                <button
                                    onClick={() => handleGrading(false)}
                                    className="premium-button-3d py-4 md:py-10 bg-gradient-to-r from-rose-500 via-red-500 to-rose-700 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-[0_20px_50px_rgba(244,63,94,0.3)] uppercase tracking-[0.2em] flex items-center justify-center gap-2 md:p-6 border-b-[12px] border-rose-800 active:translate-y-2 active:border-b-0"
                                >
                                    KLAIDA <span className="text-xl md:text-5xl animate-shake">✗</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="animate-slide-up space-y-4 md:space-y-12">
                            <div className="space-y-4">
                                <h4 className="text-[10px] font-black text-purple-500 uppercase tracking-[0.6em]">Rūšiavimo Laboratorija</h4>
                                <p className="text-base md:text-3xl font-bold text-slate-700 italic font-serif">"Sudėliok šiuos elementus pagal abėcėlę!"</p>
                            </div>

                            <div className="space-y-6 max-w-3xl mx-auto">
                                {sortedItems.map((item, index) => (
                                    <div key={index} className="flex items-center gap-3 md:p-8 bg-white p-3 md:p-8 rounded-[3.5rem] shadow-[0_30px_60px_rgba(0,0,0,0.08)] border-4 border-slate-50 transition-all hover:scale-[1.05] group relative overflow-hidden">
                                        <div className="absolute inset-0 bg-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        <div className="flex flex-col gap-3 shrink-0 relative z-10">
                                            <button
                                                onClick={() => moveItem(index, -1)}
                                                disabled={index === 0}
                                                className="w-16 h-16 bg-slate-900 text-white rounded-[1.5rem] disabled:opacity-10 flex items-center justify-center text-2xl shadow-xl active:scale-90 transition-all border-b-4 border-slate-950"
                                            >
                                                ▲
                                            </button>
                                            <button
                                                onClick={() => moveItem(index, 1)}
                                                disabled={index === sortedItems.length - 1}
                                                className="w-16 h-16 bg-slate-900 text-white rounded-[1.5rem] disabled:opacity-10 flex items-center justify-center text-2xl shadow-xl active:scale-90 transition-all border-b-4 border-slate-950"
                                            >
                                                ▼
                                            </button>
                                        </div>
                                        <div className="flex-1 text-lg md:text-4xl lg:text-xl md:text-5xl font-black text-slate-900 text-left tracking-tighter italic font-serif relative z-10 drop-shadow-sm">{item}</div>
                                        <div className="text-2xl md:text-6xl opacity-5 group-hover:opacity-20 transition-opacity relative z-10">📚</div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={handleSortingSubmit}
                                className="premium-button-3d w-full max-w-3xl py-4 md:py-10 bg-gradient-to-r from-purple-500 via-indigo-600 to-purple-800 text-white rounded-[3.5rem] font-black text-base md:text-3xl shadow-[0_30px_60px_rgba(124,58,237,0.3)] transition-all border-b-[12px] border-purple-900 uppercase tracking-[0.2em] flex items-center justify-center gap-2 md:p-6"
                            >
                                <span>PATVIRTINTI TVARKĄ</span>
                                <span className="text-lg md:text-4xl animate-bounce">✓</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Immersive Feedback Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-3xl animate-fade-in" />
                    <div className={`
                        max-w-3xl w-full p-4 md:p-20 rounded-3xl md:rounded-[5rem] bg-white shadow-[0_60px_120px_rgba(0,0,0,0.6)] relative animate-pop-in border-[16px]
                        ${feedback.type === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-48 h-48 bg-white rounded-full flex items-center justify-center text-9xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback.type === 'correct' ? '🌟' : '🧐'}
                        </div>
                        
                        <div className="text-center space-y-4 md:space-y-12 pt-14">
                            <h2 className={`text-7xl font-black uppercase tracking-tighter leading-none italic font-serif ${feedback.type === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback.type === 'correct' ? 'MOKSLO PERGALĖ!' : 'OI, KLAIDA!'}
                            </h2>

                            <div className={`p-4 md:p-12 rounded-2xl md:rounded-[4rem] border-4 text-center italic font-serif leading-relaxed ${feedback.type === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-lg md:text-4xl font-bold text-slate-800 leading-relaxed italic tracking-tight">
                                    {feedback.message}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-2 md:p-6">
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                                <div className="w-5 h-5 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TeacherHelperGame;
